import React, { useEffect, useState, useCallback, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  FaTimes,
  FaSpinner,
  FaHandPaper,
  FaMapMarkerAlt,
  FaDesktop,
  FaBan,
  FaVolumeMute,
  FaVolumeUp,
} from 'react-icons/fa';
import API from '../../API';
import intersectStaffIcon from '../images/intersect-staff-blue-icon.png';
import type { IntersectUserProfile } from './RoomUsersWidget';
import type { IVSRealtimeChatRef } from './IVSRealtimeChat';
import { sendPlayerVoiceMuteUnmute } from './playerVoiceBridge';
import { normalizeFacePinUserId } from './ivsFaceVideo';

function formatLabel(value: string | undefined | null): string {
  if (value == null || value === '') return '';
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export interface PlayerClickedModeratorContext {
  chatRef: RefObject<IVSRealtimeChatRef | null>;
  chatRoomAvailable: boolean;
  podiumPosition: string;
  targetHasRaisedHand: boolean;
  currentViewerUserId?: string;
  onModeratorUnraiseHand?: (userId: string) => void | Promise<void>;
}

export interface PlayerClickedPeopleInfoPopoverProps {
  accessToken: string;
  userId: string;
  fallbackDisplayName: string;
  onClose: () => void;
  /** Intersect staff / moderator: show per-player actions (same IVS + voice paths as People / Mod tools). */
  moderatorContext?: PlayerClickedModeratorContext;
  /** Logged-in user id (Intersect id); required for self-detection when viewer is not staff (moderatorContext may be absent). */
  viewerUserId?: string;
}

const PlayerClickedPeopleInfoPopover: React.FC<PlayerClickedPeopleInfoPopoverProps> = ({
  accessToken,
  userId,
  fallbackDisplayName,
  onClose,
  moderatorContext,
  viewerUserId,
}) => {
  const [profile, setProfile] = useState<IntersectUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modActionMessage, setModActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [voiceMutedThisTarget, setVoiceMutedThisTarget] = useState(false);
  const [unraising, setUnraising] = useState(false);
  const [modBusy, setModBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProfile(null);
    try {
      const response = await API.getIntersectUserProfile(accessToken, { userId });
      if (response.ok) {
        const data = (await response.json()) as IntersectUserProfile;
        setProfile(data);
      } else {
        setError('Profile unavailable');
      }
    } catch {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [accessToken, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setVoiceMutedThisTarget(false);
    setModActionMessage(null);
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const viewerId = normalizeFacePinUserId(viewerUserId || moderatorContext?.currentViewerUserId);
  const isTargetSelf = Boolean(viewerId && normalizeFacePinUserId(userId) === viewerId);

  const hasPodium = Boolean(moderatorContext?.podiumPosition && moderatorContext.podiumPosition.trim() !== '');
  const chat = moderatorContext?.chatRef?.current;
  const chatSendReady = Boolean(chat?.sendModeratorTeleportToUser);
  const chatScreenshareReady = Boolean(
    chat?.sendModeratorGrantScreenShare &&
      chat?.sendModeratorRevokeScreenShare &&
      chat?.isIntersectStaff?.()
  );
  const canTeleport = hasPodium && moderatorContext?.chatRoomAvailable && chatSendReady;
  const canScreenshare = Boolean(moderatorContext?.chatRoomAvailable && chatScreenshareReady);

  const showModeratorBlock = Boolean(moderatorContext && !isTargetSelf);

  const flashModMessage = (type: 'success' | 'error', text: string) => {
    setModActionMessage({ type, text });
    window.setTimeout(() => setModActionMessage(null), 4000);
  };

  const handleLowerHand = async () => {
    if (!moderatorContext?.onModeratorUnraiseHand) return;
    setUnraising(true);
    try {
      await moderatorContext.onModeratorUnraiseHand(userId);
      flashModMessage('success', 'Lowered hand for this player');
    } catch (e) {
      flashModMessage('error', e instanceof Error ? e.message : 'Could not lower hand');
    } finally {
      setUnraising(false);
    }
  };

  const handleTeleport = async () => {
    if (!chat?.sendModeratorTeleportToUser) {
      flashModMessage('error', 'Chat is not ready');
      return;
    }
    if (!window.confirm(`Teleport ${fallbackDisplayName || userId} to the room podium?`)) return;
    setModBusy(true);
    try {
      await chat.sendModeratorTeleportToUser(userId.trim());
      flashModMessage('success', 'Teleport sent');
    } catch (e) {
      flashModMessage('error', e instanceof Error ? e.message : 'Teleport failed');
    } finally {
      setModBusy(false);
    }
  };

  const handleGrantShare = async () => {
    if (!chat?.sendModeratorGrantScreenShare) {
      flashModMessage('error', 'Chat is not ready');
      return;
    }
    if (!window.confirm(`Grant screen share permission to ${fallbackDisplayName || userId}?`)) return;
    setModBusy(true);
    try {
      await chat.sendModeratorGrantScreenShare(userId.trim());
      flashModMessage('success', 'Screen share granted');
    } catch (e) {
      flashModMessage('error', e instanceof Error ? e.message : 'Request failed');
    } finally {
      setModBusy(false);
    }
  };

  const handleRevokeShare = async () => {
    if (!chat?.sendModeratorRevokeScreenShare) {
      flashModMessage('error', 'Chat is not ready');
      return;
    }
    if (!window.confirm(`Revoke screen share permission from ${fallbackDisplayName || userId}?`)) return;
    setModBusy(true);
    try {
      await chat.sendModeratorRevokeScreenShare(userId.trim());
      flashModMessage('success', 'Screen share revoked');
    } catch (e) {
      flashModMessage('error', e instanceof Error ? e.message : 'Request failed');
    } finally {
      setModBusy(false);
    }
  };

  const handleVoiceToggle = () => {
    const nextMuted = !voiceMutedThisTarget;
    const eventType = nextMuted ? 'PlayerMute' : 'PlayerUnmute';
    if (!sendPlayerVoiceMuteUnmute(eventType, userId.trim(), 'PlayerClickedPeopleInfoPopover')) {
      flashModMessage('error', 'Unity bridge not available');
      return;
    }
    setVoiceMutedThisTarget(nextMuted);
    flashModMessage(
      'success',
      nextMuted ? 'Moderator action: force-muted this player' : 'Moderator action: restored voice for this player'
    );
  };

  if (typeof document === 'undefined') return null;

  const displayName =
    profile?.username?.trim() ||
    fallbackDisplayName?.trim() ||
    userId.slice(0, 8) + '…';

  return createPortal(
    <div
      data-player-clicked-people-popover
      className="fixed inset-0 z-[10004] flex items-center justify-center bg-black/80"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <div
        className="max-h-[min(90vh,32rem)] w-[90%] max-w-[400px] overflow-y-auto overflow-x-hidden rounded-xl border border-[#333] bg-[#1e1e1e] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#333] px-4 py-3">
          <h2 className="text-lg font-semibold text-white">Profile</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-[#333] hover:text-white"
            aria-label="Close"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="p-4">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-2 py-8 text-gray-400">
              <FaSpinner className="animate-spin" size={28} />
              <span className="text-sm">Loading…</span>
              {fallbackDisplayName ? (
                <span className="text-xs text-gray-500">{fallbackDisplayName}</span>
              ) : null}
            </div>
          )}
          {error && !loading && <div className="py-6 text-center text-sm text-gray-400">{error}</div>}
          {!loading && !error && profile && (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-lg font-bold tracking-tight text-white">{displayName}</span>
                {profile.intersect_staff && (
                  <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/20 px-2 py-0.5 text-xs font-semibold text-amber-400">
                    <img src={intersectStaffIcon} alt="" className="h-3.5 w-3.5" aria-hidden />
                    Staff
                  </span>
                )}
              </div>
              <div>
                <span className="mb-0.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Membership type
                </span>
                <span className="text-white">{formatLabel(profile.membership_type) || '—'}</span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Committees
                </span>
                {profile.committees && profile.committees.length > 0 ? (
                  <ul className="space-y-0.5 text-white">
                    {profile.committees.map((c, i) => (
                      <li key={i}>
                        {formatLabel(c.properties?.name) || 'Committee'}
                        {(c.properties?.role?.name || c.properties?.role?.slug) && (
                          <span className="ml-1 text-gray-400">
                            ({formatLabel(c.properties?.role?.name || c.properties?.role?.slug)})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-gray-400">None</span>
                )}
              </div>

              {isTargetSelf && moderatorContext && (
                <p className="text-xs text-gray-500">Moderator actions are hidden for your own user.</p>
              )}

              {showModeratorBlock && moderatorContext && (
                <div className="border-t border-gray-700 pt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-400/90">
                    Moderator actions
                  </div>
                  {modActionMessage && (
                    <div
                      className={`mb-2 rounded border px-2 py-1.5 text-xs ${
                        modActionMessage.type === 'success'
                          ? 'border-green-700/60 bg-green-900/30 text-green-300'
                          : 'border-red-700/60 bg-red-900/30 text-red-300'
                      }`}
                    >
                      {modActionMessage.text}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {moderatorContext.targetHasRaisedHand && moderatorContext.onModeratorUnraiseHand && (
                      <button
                        type="button"
                        disabled={unraising || modBusy}
                        title="Lower their hand for everyone in the room"
                        onClick={() => void handleLowerHand()}
                        className="inline-flex items-center gap-1 rounded-md bg-amber-800/80 px-2 py-1.5 text-xs font-semibold text-amber-50 hover:bg-amber-700 disabled:opacity-50"
                      >
                        <FaHandPaper className="h-3 w-3 shrink-0" aria-hidden />
                        {unraising ? '…' : 'Lower hand'}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={!canTeleport || modBusy}
                      title={
                        !hasPodium
                          ? 'No podium position for this room'
                          : !moderatorContext.chatRoomAvailable
                            ? 'Chat not available'
                            : !chatSendReady
                              ? 'Waiting for chat…'
                              : 'Teleport this player to the podium'
                      }
                      onClick={() => void handleTeleport()}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-700 px-2 py-1.5 text-xs font-semibold text-white hover:bg-blue-600 disabled:opacity-40"
                    >
                      <FaMapMarkerAlt className="h-3 w-3" aria-hidden />
                      Teleport
                    </button>
                    <button
                      type="button"
                      disabled={!canScreenshare || modBusy}
                      title={
                        !moderatorContext.chatRoomAvailable
                          ? 'Chat not available'
                          : !chatScreenshareReady
                            ? 'Waiting for chat or moderator role…'
                            : 'Grant screen share permission'
                      }
                      onClick={() => void handleGrantShare()}
                      className="inline-flex items-center gap-1 rounded-md bg-green-800 px-2 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-40"
                    >
                      <FaDesktop className="h-3 w-3" aria-hidden />
                      Share on
                    </button>
                    <button
                      type="button"
                      disabled={!canScreenshare || modBusy}
                      title={
                        !moderatorContext.chatRoomAvailable
                          ? 'Chat not available'
                          : !chatScreenshareReady
                            ? 'Waiting for chat or moderator role…'
                            : 'Revoke screen share permission'
                      }
                      onClick={() => void handleRevokeShare()}
                      className="inline-flex items-center gap-1 rounded-md bg-red-900 px-2 py-1.5 text-xs font-semibold text-white hover:bg-red-800 disabled:opacity-40"
                    >
                      <FaBan className="h-3 w-3" aria-hidden />
                      Share off
                    </button>
                    <button
                      type="button"
                      disabled={modBusy}
                      title={
                        voiceMutedThisTarget
                          ? 'Moderator: restore voice for this player (force unmute)'
                          : 'Moderator: force mute this player'
                      }
                      aria-pressed={voiceMutedThisTarget}
                      onClick={handleVoiceToggle}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold text-white disabled:opacity-40 ${
                        voiceMutedThisTarget
                          ? 'bg-amber-900/90 hover:bg-amber-800'
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      {voiceMutedThisTarget ? (
                        <FaVolumeUp className="h-3 w-3" aria-hidden />
                      ) : (
                        <FaVolumeMute className="h-3 w-3" aria-hidden />
                      )}
                      {voiceMutedThisTarget ? 'Unmute' : 'Mute'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PlayerClickedPeopleInfoPopover;
